// MobilePinArt.tsx - Touch-optimized pin art canvas with haptic feedback
import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

interface Pin {
  x: number;
  y: number;
  z: number;      // Height displacement
  vz: number;     // Velocity in Z
  size: number;
  baseColor: string;
  holdTime: number; // Time remaining to hold displacement (write mode)
  imageZ: number;   // Target Z from image (photo mode)
}

type Mode = 'touch' | 'write' | 'photo' | 'zen';

interface TouchPoint {
  x: number;
  y: number;
  id: number;
}

interface MobilePinArtProps {
  width?: number;
  height?: number;
  gridSpacing?: number;
  enableHaptics?: boolean;
  primaryColor?: string;
  backgroundColor?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #0a0a0f;
  border-radius: 12px;
  overflow: hidden;
  touch-action: none; /* Prevent browser gestures */
  -webkit-user-select: none;
  user-select: none;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
`;

const HintText = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  color: #444;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  pointer-events: none;
  text-align: center;
`;

const FPSCounter = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #0f0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 4px;
`;

const ModeToggle = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${props => props.$active ? 'rgba(247, 147, 26, 0.3)' : 'rgba(0, 0, 0, 0.7)'};
  border: 2px solid ${props => props.$active ? '#f7931a' : '#333'};
  color: ${props => props.$active ? '#f7931a' : '#666'};
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModeButtons = styled.div`
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const SmallButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? 'rgba(247, 147, 26, 0.3)' : 'rgba(0, 0, 0, 0.7)'};
  border: 2px solid ${props => props.$active ? '#f7931a' : '#333'};
  color: ${props => props.$active ? '#f7931a' : '#666'};
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ColorPicker = styled.div`
  position: absolute;
  bottom: 55px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  background: rgba(0, 0, 0, 0.8);
  padding: 8px 12px;
  border-radius: 12px;
  z-index: 11;
`;

const ColorButton = styled.button<{ $color: string; $active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 3px solid ${props => props.$active ? '#fff' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 0 10px ' + props.$color : 'none'};
  
  &:active {
    transform: scale(0.9);
  }
`;

const RGBButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: 14px;
  background: ${props => props.$active 
    ? 'linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)' 
    : 'linear-gradient(90deg, #660000, #663300, #666600, #006600, #000066, #330066)'};
  border: 3px solid ${props => props.$active ? '#fff' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#888'};
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  
  &:active {
    transform: scale(0.9);
  }
`;

// ============================================================================
// Haptic Feedback Utility
// ============================================================================

const triggerHaptic = (intensity: 'light' | 'medium' | 'heavy' = 'light') => {
  // Try Vibration API first (wider support)
  if ('vibrate' in navigator) {
    const durations: Record<string, number> = {
      light: 10,
      medium: 25,
      heavy: 50
    };
    navigator.vibrate(durations[intensity]);
  }
  
  // Try iOS-specific haptic feedback (requires user gesture context)
  // This works in PWAs and some Safari contexts
  try {
    if ((window as any).webkit?.messageHandlers?.haptic) {
      (window as any).webkit.messageHandlers.haptic.postMessage(intensity);
    }
  } catch (e) {
    // Silently fail - haptics not available
  }
};

// ============================================================================
// Physics Constants
// ============================================================================

const SPRING_STIFFNESS = 0.2;
const SPRING_DAMPING = 0.88;
const NEIGHBOR_INFLUENCE = 0.012; // Smaller for ultra-fine grid
const TOUCH_RADIUS = 18;    // Smaller for finer control
const WAVE_SPEED = 50;      // Slower waves
const WAVE_WIDTH = 12;      // Finer waves
const WRITE_MODE_HOLD = 120; // 2 minutes - pins stay up very long
const WRITE_MODE_FADE = 0.008; // Super slow fade - takes ~2.5 mins to fully fade
const ULTRA_FINE_SPACING = 1.5; // Ultra-fine 1.5px spacing for maximum detail

// Zen mode pattern duration (seconds per pattern)
const ZEN_PATTERN_DURATION = 15; // Cycle through patterns
const ZEN_PATTERNS = [
  'countdown', 'breathing', 'fibonacci', 'kaleidoscope', 'interference', 'ripples', 'lissajous',
  'mandala', 'moire', 'plasma', 'harmonics', 'galaxy', 'cellular', 'waves3d', 'heart'
] as const;
const ZEN_DESCRIPTIONS: Record<typeof ZEN_PATTERNS[number], string> = {
  countdown: 'Bitcoin Conference 2026',
  breathing: 'Breathe with the ring',
  fibonacci: 'Golden spiral waves',
  kaleidoscope: '8-fold symmetry',
  interference: 'Wave interference',
  ripples: 'Dancing ripples',
  lissajous: 'Lissajous curves',
  mandala: 'Rotating flower',
  moire: 'Moiré patterns',
  plasma: 'Flowing plasma',
  harmonics: 'Harmonic oscillation',
  galaxy: 'Spiral galaxy',
  cellular: 'Cellular automata',
  waves3d: '3D wave field',
  heart: 'Beating heart'
};

// Bitcoin Conference 2026 deadline - July 24, 2026 00:00:00 UTC
const CONFERENCE_DATE = new Date('2026-07-24T00:00:00Z');

// Color palettes
type ColorMode = 'orange' | 'blue' | 'green' | 'purple' | 'pink' | 'rgb';
const COLOR_PALETTES: Record<Exclude<ColorMode, 'rgb'>, { primary: string, gradient: [number, number, number][] }> = {
  orange: { primary: '#f7931a', gradient: [[50,40,20], [247,147,26]] },
  blue: { primary: '#00bfff', gradient: [[20,40,60], [0,191,255]] },
  green: { primary: '#00ff88', gradient: [[20,50,30], [0,255,136]] },
  purple: { primary: '#bf5fff', gradient: [[40,20,60], [191,95,255]] },
  pink: { primary: '#ff69b4', gradient: [[60,20,40], [255,105,180]] },
};

// ============================================================================
// Main Component
// ============================================================================

export const MobilePinArt: React.FC<MobilePinArtProps> = ({
  width,
  height,
  gridSpacing = ULTRA_FINE_SPACING,
  enableHaptics = true,
  primaryColor = '#f7931a',
  backgroundColor = '#0a0a0f'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  const [dimensions, setDimensions] = useState({ width: 400, height: 600 });
  const [fps, setFps] = useState(0);
  const [mode, setMode] = useState<Mode>('touch');
  const [zenPatternIndex, setZenPatternIndex] = useState(0);
  const [colorMode, setColorMode] = useState<ColorMode>('orange');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const modeRef = useRef<Mode>('touch');
  const pulseTimeRef = useRef(0);
  const zenTimeRef = useRef(0);
  const zenPatternRef = useRef(0);
  const rgbTimeRef = useRef(0);
  const colorModeRef = useRef<ColorMode>('orange');
  const countdownCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const countdownDataRef = useRef<ImageData | null>(null);
  const lastCountdownSecondRef = useRef<number>(-1);
  
  // State refs for animation loop
  const pinsRef = useRef<Pin[]>([]);
  const touchesRef = useRef<TouchPoint[]>([]);
  const wavesRef = useRef<Array<{ time: number, x: number, y: number }>>([]);
  const lastHapticRef = useRef(0);
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameRef = useRef(performance.now());
  const gridInfoRef = useRef({ cols: 0, rows: 0 });
  
  // ============================================================================
  // Initialize Pin Grid
  // ============================================================================
  
  useEffect(() => {
    if (dimensions.width === 0) return;
    
    const pins: Pin[] = [];
    const padding = 15;
    const areaWidth = dimensions.width - padding * 2;
    const areaHeight = dimensions.height - padding * 2 - 60; // Leave room for hint
    
    const cols = Math.floor(areaWidth / gridSpacing);
    const rows = Math.floor(areaHeight / gridSpacing);
    
    const spacingX = areaWidth / Math.max(1, cols - 1);
    const spacingY = areaHeight / Math.max(1, rows - 1);
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = padding + col * spacingX;
        const y = padding + row * spacingY;
        
        // Subtle random darkness variation
        const rand = Math.random();
        const baseColor = rand > 0.7 ? '#1a1a1a' : rand > 0.4 ? '#141414' : '#0f0f0f';
        
        pins.push({
          x, y,
          z: 0,
          vz: 0,
          size: 0.8, // Tiny for hyper-fine 2px grid
          baseColor,
          holdTime: 0,
          imageZ: 0
        });
      }
    }
    
    pinsRef.current = pins;
    gridInfoRef.current = { cols, rows };
  }, [dimensions, gridSpacing]);
  
  // ============================================================================
  // Touch Handlers
  // ============================================================================
  
  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Trigger haptic on first touch
    if (enableHaptics && e.touches.length === 1) {
      triggerHaptic('light');
    }
    
    // Add a new wave from first touch point (only in touch mode)
    if (touches.length > 0 && modeRef.current === 'touch') {
      wavesRef.current.push({
        time: 0.001,
        x: touches[0].x,
        y: touches[0].y
      });
    }
    
    // In zen mode, touch changes to next pattern
    if (modeRef.current === 'zen') {
      const nextPattern = (zenPatternRef.current + 1) % ZEN_PATTERNS.length;
      zenPatternRef.current = nextPattern;
      zenTimeRef.current = 0; // Reset time for smooth transition
      // Trigger state update to refresh hint text
      setZenPatternIndex(nextPattern);
    }
  }, [dimensions, enableHaptics]);
  
  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Throttled haptic feedback while moving
    if (enableHaptics) {
      const now = performance.now();
      if (now - lastHapticRef.current > 100) {
        // Check if any pin is highly displaced
        const maxZ = pinsRef.current.reduce((max, p) => Math.max(max, Math.abs(p.z)), 0);
        if (maxZ > 2) {
          triggerHaptic('light');
          lastHapticRef.current = now;
        }
      }
    }
  }, [dimensions, enableHaptics]);
  
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Haptic on release
    if (enableHaptics && e.touches.length === 0) {
      triggerHaptic('medium');
    }
  }, [dimensions, enableHaptics]);
  
  // ============================================================================
  // Animation Loop
  // ============================================================================
  
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    const pins = pinsRef.current;
    const touches = touchesRef.current;
    const waves = wavesRef.current;
    const { cols, rows } = gridInfoRef.current;
    const { width: w, height: h } = dimensions;
    
    // Update all waves and remove expired ones
    for (let i = waves.length - 1; i >= 0; i--) {
      waves[i].time += 0.016;
      if (waves[i].time > 8) { // Longer lifetime for visual effect
        waves.splice(i, 1);
      }
    }
    
    const currentMode = modeRef.current;
    
    // Update pulse time for photo mode
    pulseTimeRef.current += 0.016;
    const pulsePhase = Math.sin(pulseTimeRef.current * 3) * 0.5 + 0.5; // 0-1 pulsing
    
    // Zen mode pattern time
    if (currentMode === 'zen') {
      zenTimeRef.current += 0.016;
      // Cycle through patterns
      if (zenTimeRef.current > ZEN_PATTERN_DURATION) {
        zenTimeRef.current = 0;
        zenPatternRef.current = (zenPatternRef.current + 1) % ZEN_PATTERNS.length;
      }
    }
    
    // Pre-generate countdown canvas ONCE per second (not per pin!)
    const zenPattern = ZEN_PATTERNS[zenPatternRef.current];
    if (zenPattern === 'countdown' && currentMode === 'zen') {
      const now = Date.now();
      const diff = CONFERENCE_DATE.getTime() - now;
      const currentSecond = Math.floor(diff / 1000);
      
      if (currentSecond !== lastCountdownSecondRef.current && diff > 0) {
        lastCountdownSecondRef.current = currentSecond;
        const { cols, rows } = gridInfoRef.current;
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        if (!countdownCanvasRef.current) {
          countdownCanvasRef.current = document.createElement('canvas');
        }
        const textCanvas = countdownCanvasRef.current;
        
        // Render at 4x resolution for supersampling
        const scale = 4;
        textCanvas.width = cols * scale;
        textCanvas.height = rows * scale;
        const textCtx = textCanvas.getContext('2d');
        
        if (textCtx) {
          // Disable anti-aliasing for crisp edges
          textCtx.imageSmoothingEnabled = false;
          
          textCtx.fillStyle = '#000';
          textCtx.fillRect(0, 0, textCanvas.width, textCanvas.height);
          
          const line1 = `${days}d ${hours}h`;
          const line2 = `${minutes}m ${seconds}s`;
          
          textCtx.fillStyle = '#fff';
          textCtx.textAlign = 'center';
          textCtx.textBaseline = 'middle';
          
          // Larger font for crisp rendering
          const fontSize = Math.min(textCanvas.width / 5, textCanvas.height / 4);
          textCtx.font = `bold ${fontSize}px monospace`;
          
          textCtx.fillText(line1, textCanvas.width / 2, textCanvas.height * 0.35);
          textCtx.fillText(line2, textCanvas.width / 2, textCanvas.height * 0.65);
          
          // Get high-res data then downsample with threshold
          const hiResData = textCtx.getImageData(0, 0, textCanvas.width, textCanvas.height);
          
          // Create final pin-resolution data with binary threshold
          const finalData = new ImageData(cols, rows);
          for (let py = 0; py < rows; py++) {
            for (let px = 0; px < cols; px++) {
              // Sample the center of this pin's corresponding hi-res area
              const hx = Math.floor(px * scale + scale / 2);
              const hy = Math.floor(py * scale + scale / 2);
              const hiIdx = (hy * textCanvas.width + hx) * 4;
              const brightness = hiResData.data[hiIdx];
              
              // Binary threshold: > 128 = fully on, else fully off
              const finalIdx = (py * cols + px) * 4;
              const isOn = brightness > 128 ? 255 : 0;
              finalData.data[finalIdx] = isOn;
              finalData.data[finalIdx + 1] = isOn;
              finalData.data[finalIdx + 2] = isOn;
              finalData.data[finalIdx + 3] = 255;
            }
          }
          countdownDataRef.current = finalData;
        }
      }
    }
    
    // Physics update
    for (let i = 0; i < pins.length; i++) {
      const p = pins[i];
      
      // Decay hold time
      if (p.holdTime > 0) {
        p.holdTime -= 0.016 * WRITE_MODE_FADE;
        if (p.holdTime < 0) p.holdTime = 0;
      }
      
      // Wave effects from ALL active waves (ONLY in touch mode)
      if (currentMode === 'touch') {
        for (const wave of waves) {
          const waveRadius = wave.time * WAVE_SPEED;
          const waveStrength = Math.max(0, 1 - wave.time / 8) * 8;
          
          const dx = p.x - wave.x;
          const dy = p.y - wave.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const distFromWave = Math.abs(dist - waveRadius);
          
          if (distFromWave < WAVE_WIDTH) {
            const phase = (distFromWave / WAVE_WIDTH) * Math.PI;
            // If pin is being held from write mode, wave just highlights (pushes less)
            const holdFactor = p.holdTime > 0 ? 0.3 : 1;
            p.vz += Math.sin(phase) * waveStrength * 0.12 * holdFactor;
          }
        }
      }
      
      // Photo mode: pins follow image silhouette with pulsation
      if (currentMode === 'photo' && p.imageZ > 0) {
        const targetZ = p.imageZ * (0.7 + pulsePhase * 0.6); // Pulsate between 70-130%
        p.vz += (targetZ - p.z) * 0.15;
      }
      
      // Zen mode: geometric patterns
      if (currentMode === 'zen') {
        const centerX = w / 2;
        const centerY = (h - 80) / 2; // Account for buttons at bottom
        const dx = p.x - centerX;
        const dy = p.y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const t = zenTimeRef.current;
        const pattern = ZEN_PATTERNS[zenPatternRef.current];
        const maxRadius = Math.min(w, h) * 0.4;
        
        let targetZ = 0;
        
        switch (pattern) {
          case 'countdown': {
            // Sample pre-generated text pixel for this pin
            if (countdownDataRef.current) {
              const { cols: gridCols } = gridInfoRef.current;
              const pinCol = i % gridCols;
              const pinRow = Math.floor(i / gridCols);
              const pixelIdx = (pinRow * gridCols + pinCol) * 4;
              const isOn = countdownDataRef.current.data[pixelIdx] > 128;
              
              // Binary: fully raised or completely flat
              // Subtle pulse only on the raised pins
              if (isOn) {
                const pulse = Math.sin(t * 2) * 0.1 + 0.95;
                targetZ = 6 * pulse;
              }
            }
            break;
          }
          case 'breathing': {
            // Single circle that expands and contracts like breathing
            const breathCycle = 4;
            const breathPhase = (t % breathCycle) / breathCycle;
            const breathRadius = maxRadius * (0.3 + 0.7 * (Math.sin(breathPhase * Math.PI * 2 - Math.PI / 2) * 0.5 + 0.5));
            const ringWidth = 25; // Finer ring
            const distFromRing = Math.abs(dist - breathRadius);
            
            if (distFromRing < ringWidth) {
              const ringIntensity = 1 - (distFromRing / ringWidth);
              targetZ = ringIntensity * ringIntensity * 7;
            }
            break;
          }
          case 'fibonacci': {
            // Golden ratio spiral with propagating waves
            const phi = 1.618033988749895;
            const spiralAngle = angle + t * 0.5;
            const spiralR = Math.log(dist + 1) / Math.log(phi);
            const wave = Math.sin(spiralR * 2 - spiralAngle * 3 + t * 2);
            const envelope = Math.exp(-dist / (maxRadius * 0.8));
            targetZ = wave * 5 * envelope;
            break;
          }
          case 'kaleidoscope': {
            // 8-fold radial symmetry
            const segments = 8;
            const segmentAngle = (angle + Math.PI) % (Math.PI * 2 / segments);
            const mirroredAngle = segmentAngle < Math.PI / segments ? segmentAngle : Math.PI * 2 / segments - segmentAngle;
            const radialWave = Math.sin(dist * 0.04 - t * 3);
            const angularWave = Math.sin(mirroredAngle * segments * 2 + t * 2);
            const combined = (radialWave + angularWave) / 2;
            const envelope = Math.exp(-dist / (maxRadius * 0.9));
            targetZ = combined * 5 * envelope;
            break;
          }
          case 'interference': {
            // Multiple sine wave interference
            const wave1 = Math.sin(dx * 0.025 + t * 2);
            const wave2 = Math.sin(dy * 0.025 + t * 2.3);
            const wave3 = Math.sin((dx + dy) * 0.018 - t * 1.7);
            const wave4 = Math.sin((dx - dy) * 0.018 + t * 1.5);
            const combined = (wave1 + wave2 + wave3 + wave4) / 4;
            targetZ = combined * 6;
            break;
          }
          case 'ripples': {
            // Multiple concentric ripples from moving centers
            const cx1 = centerX + Math.sin(t * 0.7) * w * 0.2;
            const cy1 = centerY + Math.cos(t * 0.7) * h * 0.15;
            const cx2 = centerX + Math.sin(t * 0.5 + 2) * w * 0.15;
            const cy2 = centerY + Math.cos(t * 0.5 + 2) * h * 0.12;
            const cx3 = centerX + Math.sin(t * 0.3 + 4) * w * 0.1;
            const cy3 = centerY + Math.cos(t * 0.3 + 4) * h * 0.08;
            
            const d1 = Math.sqrt((p.x - cx1) ** 2 + (p.y - cy1) ** 2);
            const d2 = Math.sqrt((p.x - cx2) ** 2 + (p.y - cy2) ** 2);
            const d3 = Math.sqrt((p.x - cx3) ** 2 + (p.y - cy3) ** 2);
            
            const r1 = Math.sin(d1 * 0.035 - t * 3.5) * Math.exp(-d1 / (w * 0.5));
            const r2 = Math.sin(d2 * 0.04 - t * 2.8) * Math.exp(-d2 / (w * 0.45));
            const r3 = Math.sin(d3 * 0.045 - t * 4) * Math.exp(-d3 / (w * 0.4));
            
            targetZ = (r1 + r2 + r3) * 4;
            break;
          }
          case 'lissajous': {
            // Lissajous curve pattern
            const freqX = 3;
            const freqY = 4;
            const phase = t * 0.5;
            
            const lx = centerX + Math.sin(freqX * t + phase) * w * 0.3;
            const ly = centerY + Math.sin(freqY * t) * h * 0.2;
            const lx2 = centerX + Math.sin(5 * t + phase * 0.7) * w * 0.25;
            const ly2 = centerY + Math.sin(7 * t) * h * 0.18;
            
            const dLiss1 = Math.sqrt((p.x - lx) ** 2 + (p.y - ly) ** 2);
            const dLiss2 = Math.sqrt((p.x - lx2) ** 2 + (p.y - ly2) ** 2);
            
            const influence1 = Math.exp(-dLiss1 / 40) * 6;
            const influence2 = Math.exp(-dLiss2 / 35) * 5;
            
            targetZ = influence1 + influence2;
            break;
          }
          case 'mandala': {
            // Rotating flower mandala with fine petals
            const petals = 8;
            const rotationSpeed = 0.25;
            const rotatedAngle = angle + t * rotationSpeed;
            
            const petalAngle = Math.abs(Math.sin(rotatedAngle * petals));
            const petalRadius = maxRadius * (0.3 + 0.6 * petalAngle);
            
            const layer1 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.5) / 20);
            const layer2 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.75) / 18);
            const layer3 = Math.max(0, 1 - Math.abs(dist - petalRadius) / 15);
            
            const centerPulse = Math.exp(-dist / 30) * (Math.sin(t * 2) * 0.3 + 0.7);
            
            targetZ = (layer1 * 3 + layer2 * 4 + layer3 * 5 + centerPulse * 6) * 0.45;
            break;
          }
          case 'moire': {
            // Moiré interference pattern
            const freq1 = 0.03;
            const freq2 = 0.031;
            const rotation = t * 0.2;
            
            const rx = dx * Math.cos(rotation) - dy * Math.sin(rotation);
            const ry = dx * Math.sin(rotation) + dy * Math.cos(rotation);
            
            const pattern1 = Math.sin(dx * freq1 * 2 * Math.PI);
            const pattern2 = Math.sin(rx * freq2 * 2 * Math.PI);
            const pattern3 = Math.sin(dist * 0.02 - t);
            
            targetZ = ((pattern1 + pattern2) / 2 + pattern3 * 0.5) * 4;
            break;
          }
          case 'plasma': {
            // Classic plasma effect
            const plasma1 = Math.sin(p.x * 0.02 + t);
            const plasma2 = Math.sin(p.y * 0.02 + t * 1.1);
            const plasma3 = Math.sin((p.x + p.y) * 0.015 + t * 0.7);
            const plasma4 = Math.sin(dist * 0.02 - t * 0.5);
            
            const plasma = (plasma1 + plasma2 + plasma3 + plasma4) / 4;
            targetZ = plasma * 5 + 2;
            break;
          }
          case 'harmonics': {
            // Multiple harmonic oscillations
            const h1 = Math.sin(dist * 0.03 - t * 2);
            const h2 = Math.sin(dist * 0.06 - t * 3) * 0.5;
            const h3 = Math.sin(dist * 0.09 - t * 4) * 0.25;
            const h4 = Math.sin(dist * 0.12 - t * 5) * 0.125;
            
            const harmonic = h1 + h2 + h3 + h4;
            const env = Math.exp(-dist / (maxRadius * 1.2));
            targetZ = harmonic * 4 * env;
            break;
          }
          case 'galaxy': {
            // Spiral galaxy with arms
            const arms = 2;
            const spiralTightness = 0.008;
            const rotationSpeed = 0.3;
            
            const spiralAngle = angle + t * rotationSpeed;
            let armDist = Infinity;
            
            for (let arm = 0; arm < arms * 3; arm++) {
              const armAngle = (arm / arms) * Math.PI * 2;
              const expectedRadius = (spiralAngle - armAngle + Math.PI * 6) * spiralTightness * maxRadius;
              if (expectedRadius > 0 && expectedRadius < maxRadius * 1.5) {
                armDist = Math.min(armDist, Math.abs(dist - expectedRadius));
              }
            }
            
            const armWidth = 25 + dist * 0.1;
            if (armDist < armWidth) {
              const intensity = (1 - armDist / armWidth) * Math.exp(-dist / (maxRadius * 1.5));
              targetZ = intensity * 6;
            }
            
            // Bright center
            const centerGlow = Math.exp(-dist / 40) * 5;
            targetZ = Math.max(targetZ, centerGlow);
            break;
          }
          case 'cellular': {
            // Cellular automata-like pattern
            const cellSize = 30;
            const cellX = Math.floor(p.x / cellSize);
            const cellY = Math.floor(p.y / cellSize);
            
            // Pseudo-random based on position and time
            const seed = (cellX * 127 + cellY * 311 + Math.floor(t * 2)) % 100;
            const alive = seed < 40;
            
            if (alive) {
              const localX = (p.x % cellSize) / cellSize;
              const localY = (p.y % cellSize) / cellSize;
              const edgeDist = Math.min(localX, 1-localX, localY, 1-localY) * 2;
              targetZ = edgeDist * 6;
            }
            break;
          }
          case 'waves3d': {
            // 3D wave field effect
            const waveX = Math.sin(p.x * 0.025 + t * 2);
            const waveY = Math.sin(p.y * 0.03 + t * 1.7);
            const waveDiag = Math.sin((p.x - p.y) * 0.02 + t * 2.3);
            
            // Combine with depth-like effect
            const depth = waveX * waveY;
            const surface = (waveX + waveY + waveDiag) / 3;
            
            targetZ = (depth * 3 + surface * 3) * 0.8;
            break;
          }
          case 'heart': {
            // Beating heart using implicit equation
            const scale = maxRadius * 0.011;
            const heartX = (p.x - centerX) / scale;
            const heartY = -(p.y - centerY) / scale + 10;
            
            const heartEq = Math.pow(heartX * heartX + heartY * heartY - 100, 3) - heartX * heartX * Math.pow(heartY, 3) * 100;
            const heartDist = heartEq / 50000;
            
            const beat = Math.sin(t * 4) * 0.25 + 1;
            
            if (heartDist < 0) {
              const intensity = Math.min(1, -heartDist / 8) * beat;
              targetZ = intensity * 7;
            } else if (heartDist < 4) {
              targetZ = (1 - heartDist / 4) * 3 * beat;
            }
            break;
          }
        }
        
        // Smooth transition to target
        p.vz += (targetZ - p.z) * 0.08;
      }
      
      // Touch influence (touch and write modes)
      if (currentMode !== 'photo') {
        for (const touch of touches) {
          const dx = p.x - touch.x;
          const dy = p.y - touch.y;
          const distSq = dx * dx + dy * dy;
          const radiusSq = TOUCH_RADIUS * TOUCH_RADIUS;
          
          if (distSq < radiusSq) {
            const influence = 1 - distSq / radiusSq;
            
            if (currentMode === 'write') {
              // Write mode: set pin to raised state and hold it
              const targetZ = 6 * influence;
              if (targetZ > p.z) {
                p.z = targetZ;
                p.vz = 0;
              }
              p.holdTime = WRITE_MODE_HOLD;
            } else {
              // Touch mode: just push
              p.vz += influence * 1.2;
            }
          }
        }
      }
      
      // Spring physics - much slower return when holding or in photo/zen mode
      // When holdTime is active, pins barely move back at all
      const springMultiplier = (p.holdTime > 0) ? 0.005 : 
        (currentMode === 'photo' && p.imageZ > 0) ? 0.02 : 
        (currentMode === 'zen') ? 0.08 : 1;
      p.vz -= p.z * SPRING_STIFFNESS * springMultiplier;
      p.vz *= SPRING_DAMPING;
      p.z += p.vz;
      p.z = Math.max(-8, Math.min(12, p.z));
    }
    
    // Neighbor influence
    for (let row = 1; row < rows - 1; row++) {
      for (let col = 1; col < cols - 1; col++) {
        const idx = row * cols + col;
        if (idx >= pins.length) continue;
        
        const p = pins[idx];
        const top = pins[(row - 1) * cols + col];
        const bottom = pins[(row + 1) * cols + col];
        const left = pins[row * cols + (col - 1)];
        const right = pins[row * cols + (col + 1)];
        
        if (top && bottom && left && right) {
          const avg = (top.z + bottom.z + left.z + right.z) / 4;
          p.vz += (avg - p.z) * NEIGHBOR_INFLUENCE;
        }
      }
    }
    
    // RGB animation
    rgbTimeRef.current += 0.016;
    const currentColorMode = colorModeRef.current;
    
    // Draw
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, w, h);
    
    // Group by color for batching
    const colorBuckets = new Map<string, Array<{x: number, y: number, size: number}>>();
    
    // Get color function based on mode
    const getColor = (absZ: number, x: number, y: number): string => {
      if (absZ < 0.4) return '#1a1a1a';
      
      if (currentColorMode === 'rgb') {
        // Rainbow RGB cycling based on position and time
        const rgbTime = rgbTimeRef.current;
        const hue = ((x + y) * 0.5 + rgbTime * 100) % 360;
        const saturation = 80 + absZ * 4;
        const lightness = 20 + absZ * 12;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      }
      
      const palette = COLOR_PALETTES[currentColorMode];
      const [lowR, lowG, lowB] = palette.gradient[0];
      const [highR, highG, highB] = palette.gradient[1];
      
      if (absZ > 2.5) {
        return palette.primary;
      } else if (absZ > 1.2) {
        const intensity = (absZ - 1.2) / 1.3;
        const r = Math.round(lowR + (highR - lowR) * intensity);
        const g = Math.round(lowG + (highG - lowG) * intensity);
        const b = Math.round(lowB + (highB - lowB) * intensity);
        return `rgb(${r},${g},${b})`;
      } else {
        const intensity = (absZ - 0.4) / 0.8;
        const r = Math.round(25 + (lowR - 25) * intensity);
        const g = Math.round(20 + (lowG - 20) * intensity);
        const b = Math.round(15 + (lowB - 15) * intensity);
        return `rgb(${r},${g},${b})`;
      }
    };
    
    for (const p of pins) {
      const absZ = Math.abs(p.z);
      const displaySize = p.size + absZ * 0.4;
      const displayY = p.y - p.z * 0.8;
      
      const color = getColor(absZ, p.x, p.y);
      
      if (!colorBuckets.has(color)) {
        colorBuckets.set(color, []);
      }
      colorBuckets.get(color)!.push({ x: p.x, y: displayY, size: displaySize });
    }
    
    // Draw batched
    colorBuckets.forEach((pinList, color) => {
      ctx.fillStyle = color;
      for (const pin of pinList) {
        ctx.fillRect(pin.x - pin.size, pin.y - pin.size, pin.size * 2, pin.size * 2);
      }
    });
    
    // Draw touch points (debug)
    ctx.fillStyle = 'rgba(247, 147, 26, 0.3)';
    for (const touch of touches) {
      ctx.beginPath();
      ctx.arc(touch.x, touch.y, TOUCH_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // FPS tracking
    const now = performance.now();
    frameTimesRef.current.push(now - lastFrameRef.current);
    lastFrameRef.current = now;
    if (frameTimesRef.current.length > 60) frameTimesRef.current.shift();
    
    if (frameTimesRef.current.length >= 30) {
      const avgFrame = frameTimesRef.current.reduce((a, b) => a + b) / frameTimesRef.current.length;
      setFps(Math.round(1000 / avgFrame));
    }
    
    animationRef.current = requestAnimationFrame(render);
  }, [dimensions, primaryColor, backgroundColor]);
  
  // ============================================================================
  // Effects
  // ============================================================================
  
  // Resize observer
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: width || clientWidth || 400,
          height: height || clientHeight || 600
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height]);
  
  // Canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    
    // Start animation
    animationRef.current = requestAnimationFrame(render);
    
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [dimensions, render]);
  
  // Touch event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  // ============================================================================
  // Render
  // ============================================================================
  
  // Sync mode ref
  useEffect(() => {
    modeRef.current = mode;
    // Clear image data when leaving photo mode
    if (mode !== 'photo') {
      pinsRef.current.forEach(p => p.imageZ = 0);
    }
  }, [mode]);
  
  // Sync color mode ref
  useEffect(() => {
    colorModeRef.current = colorMode;
  }, [colorMode]);
  
  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    if (newMode === 'zen') {
      zenTimeRef.current = 0;
      zenPatternRef.current = 0;
    }
    if (enableHaptics) triggerHaptic('medium');
  }, [enableHaptics]);
  
  const handlePhotoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if grid is initialized
    const { cols, rows } = gridInfoRef.current;
    if (cols === 0 || rows === 0) {
      console.warn('Grid not ready yet, cols:', cols, 'rows:', rows);
      alert('Please wait for the grid to load and try again');
      return;
    }
    
    console.log('Photo upload started, grid:', cols, 'x', rows, 'pins:', pinsRef.current.length);
    
    const img = new Image();
    // Don't set crossOrigin for blob URLs - it can cause issues
    
    img.onload = () => {
      console.log('Image loaded:', img.width, 'x', img.height);
      
      // Create offscreen canvas for image processing
      if (!imageCanvasRef.current) {
        imageCanvasRef.current = document.createElement('canvas');
      }
      const canvas = imageCanvasRef.current;
      canvas.width = cols;
      canvas.height = rows;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Could not get canvas context');
        return;
      }
      
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, cols, rows);
      
      // Draw image scaled to grid size
      const scale = Math.min(cols / img.width, rows / img.height);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      const offsetX = (cols - drawW) / 2;
      const offsetY = (rows - drawH) / 2;
      ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
      
      // Get image data and compute brightness for each pin
      const imageData = ctx.getImageData(0, 0, cols, rows);
      const pins = pinsRef.current;
      
      let affectedPins = 0;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const idx = row * cols + col;
          if (idx >= pins.length) continue;
          
          const pixelIdx = (row * cols + col) * 4;
          const r = imageData.data[pixelIdx];
          const g = imageData.data[pixelIdx + 1];
          const b = imageData.data[pixelIdx + 2];
          
          // Convert to grayscale brightness (0-1)
          const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
          
          // Use brightness directly - bright areas push pins up
          // More permissive threshold for better results
          const heightValue = brightness > 0.1 ? brightness * 8 : 0;
          
          pins[idx].imageZ = heightValue;
          if (heightValue > 0) affectedPins++;
        }
      }
      
      console.log('Photo processed, affected pins:', affectedPins, '/', pins.length);
      setMode('photo');
      if (enableHaptics) triggerHaptic('heavy');
    };
    
    img.onerror = (err) => {
      console.error('Image load error:', err);
      alert('Could not load image. Try a different image format (JPG/PNG).');
    };
    
    // Create blob URL and load
    const blobUrl = URL.createObjectURL(file);
    console.log('Loading image from blob:', blobUrl);
    img.src = blobUrl;
    
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [enableHaptics]);
  
  const getModeIcon = (m: Mode) => {
    switch (m) {
      case 'touch': return '👆';
      case 'write': return '✍️';
      case 'photo': return '📷';
      case 'zen': return '🧘';
    }
  };
  
  const getHintText = () => {
    switch (mode) {
      case 'touch': return 'Touch and swipe to interact';
      case 'write': return 'Draw to make pins stay raised';
      case 'photo': return 'Pins pulsate to show your image';
      case 'zen': return `${ZEN_DESCRIPTIONS[ZEN_PATTERNS[zenPatternIndex]]} • Tap to change`;
    }
  };
  
  return (
    <Container ref={containerRef}>
      <Canvas ref={canvasRef} />
      <ModeButtons>
        <SmallButton $active={mode === 'touch'} onClick={() => handleModeChange('touch')}>
          👆 Touch
        </SmallButton>
        <SmallButton $active={mode === 'write'} onClick={() => handleModeChange('write')}>
          ✍️ Write
        </SmallButton>
        <SmallButton $active={mode === 'photo'} onClick={() => fileInputRef.current?.click()}>
          📷 Photo
        </SmallButton>
        <SmallButton $active={mode === 'zen'} onClick={() => handleModeChange('zen')}>
          🧘 Zen
        </SmallButton>
        <SmallButton 
          $active={showColorPicker} 
          onClick={() => setShowColorPicker(!showColorPicker)}
          style={{ marginLeft: 'auto' }}
        >
          🎨
        </SmallButton>
      </ModeButtons>
      {showColorPicker && (
        <ColorPicker>
          <ColorButton $color="#f7931a" $active={colorMode === 'orange'} onClick={() => setColorMode('orange')} />
          <ColorButton $color="#00bfff" $active={colorMode === 'blue'} onClick={() => setColorMode('blue')} />
          <ColorButton $color="#00ff88" $active={colorMode === 'green'} onClick={() => setColorMode('green')} />
          <ColorButton $color="#bf5fff" $active={colorMode === 'purple'} onClick={() => setColorMode('purple')} />
          <ColorButton $color="#ff69b4" $active={colorMode === 'pink'} onClick={() => setColorMode('pink')} />
          <RGBButton $active={colorMode === 'rgb'} onClick={() => setColorMode('rgb')}>
            RGB
          </RGBButton>
        </ColorPicker>
      )}
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoUpload}
      />
      <HintText>{getHintText()}</HintText>
      <FPSCounter>{fps} FPS | {pinsRef.current.length} pins</FPSCounter>
    </Container>
  );
};

export default MobilePinArt;
